import { Sidebar } from "lucide-react";

const fonts = {
  fontFamily: "'Inter', sans-serif",
  fontSize: "14px",
  fontWeightLight: "300",
  fontWeightRegular: "400",
  fontWeightMedium: "500",
  fontWeightBold: "700",
  fontWeightExtraBold: "800",
};

const border = {
  sm: "0.5px",
  md: "1px",
  lg: "2px",
};

const typography = {
  default: {
    fontFamily: fonts.fontFamily,
    fontSize: "16px",
    fontWeight: fonts.fontWeightRegular,
    lineHeight: "24px",
    mobileSize: "14px",
    mobileLineHeight: "22px",
    letterSpacing: "normal",
  },

  hero: {
    fontSize: "clamp(36px, 5vw, 48px)", // ✅ Responsive size
    fontWeight: fonts.fontWeightExtraBold,
    lineHeight: "clamp(44px, 6vw, 56px)",
    letterSpacing: "-0.5px",
    mobileSize: "32px",
    mobileLineHeight: "40px",
    
  },

  display: {
    fontSize: "clamp(32px, 4.5vw, 40px)",
    fontWeight: fonts.fontWeightBold,
    lineHeight: "clamp(40px, 5vw, 48px)",
    letterSpacing: "-0.4px",
    mobileSize: "28px",
    mobileLineHeight: "36px",
  },

  title: {
    fontSize: "clamp(28px, 3.5vw, 32px)",
    fontWeight: fonts.fontWeightBold,
    lineHeight: "40px",
    letterSpacing: "-0.3px",
    mobileSize: "24px",
    mobileLineHeight: "32px",
  },
  
  heading1: {
    fontSize: "clamp(28px, 3.5vw, 32px)",
    fontWeight: fonts.fontWeightBold,
    lineHeight: "40px",
    letterSpacing: "-0.3px",
    mobileSize: "24px",
    mobileLineHeight: "32px",
  },

  heading2: {
    fontSize: "clamp(22px, 2.5vw, 24px)",
    fontWeight: fonts.fontWeightMedium,
    lineHeight: "32px",
    letterSpacing: "-0.2px",
    mobileSize: "20px",
    mobileLineHeight: "28px",
  },

  heading3: {
    fontSize: "clamp(18px, 2vw, 20px)",
    fontWeight: fonts.fontWeightMedium,
    lineHeight: "28px",
    letterSpacing: "-0.1px",
    mobileSize: "18px",
    mobileLineHeight: "26px",
  },
  heading4: {
    fontSize: "clamp(18px, 2vw, 20px)",
    fontWeight: fonts.fontWeightMedium,
    lineHeight: "20px",
    letterSpacing: "-0.1px",
    mobileSize: "16px",
    mobileLineHeight: "22px",
  },

  bodyLarge: {
    fontSize: "18px",
    fontWeight: fonts.fontWeightRegular,
    lineHeight: "26px",
    mobileSize: "16px",
    mobileLineHeight: "24px",
    letterSpacing: "normal", // ✅ Add this

  },

  body: {
    fontSize: "16px",
    fontWeight: fonts.fontWeightRegular,
    lineHeight: "24px",
    mobileSize: "14px",
    mobileLineHeight: "22px",
    letterSpacing: "normal", // ✅ Add this

  },

  bodySmall: {
    fontSize: "14px", // ✅ Increased for better readability
    fontWeight: fonts.fontWeightRegular,
    lineHeight: "18px",
    mobileSize: "12px",
    mobileLineHeight: "16px",
    letterSpacing: "normal", // ✅ Add this

  },

  caption: {
    fontSize: "13px", // ✅ Increased slightly for readability
    fontWeight: fonts.fontWeightRegular,
    lineHeight: "18px",
    mobileSize: "12px",
    mobileLineHeight: "16px",
    letterSpacing: "normal", // ✅ Add this

  },
  
  quote: {
    fontSize: "13px", // ✅ Increased slightly for readability
    fontWeight: fonts.fontWeightRegular,
    lineHeight: "18px",
    mobileSize: "12px",
    mobileLineHeight: "16px",
    letterSpacing: "normal", // ✅ Add this

  },

  overline: {
    fontSize: "10px",
    fontWeight: fonts.fontWeightBold,
    textTransform: "uppercase",
    lineHeight: "14px",
    mobileSize: "10px",
    mobileLineHeight: "14px",
    letterSpacing: "normal", // ✅ Add this

  },

  disclaimer: {
    fontSize: "12px",
    fontWeight: fonts.fontWeightLight,
    lineHeight: "16px",
    mobileSize: "12px",
    mobileLineHeight: "16px",
    letterSpacing: "normal", // ✅ Add this

  },

  legalText: {
    fontSize: "10px",
    fontWeight: fonts.fontWeightLight,
    lineHeight: "14px",
    mobileSize: "10px",
    mobileLineHeight: "14px",
    letterSpacing: "normal", // ✅ Add this

  },
  
  error: {
    fontSize: "10px",
    fontWeight: fonts.fontWeightLight,
    lineHeight: "14px",
    mobileSize: "10px",
    mobileLineHeight: "14px",
    letterSpacing: "normal", // ✅ Add this

  },
};
const zIndices = {
  navbar: 10,
  sidebar: 9,
  drawer: 9,
}
const spacing = {
  xxs: "2px",
  xs: "4px",
  sm: "8px",
  md: "16px",
  lg: "24px",
  xl: "32px",
  xxl: "40px",
  sidebarWidth: "200px",
};

const radii = {
  sm: "4px",
  md: "8px",
  lg: "12px",
  rounded: "100px",
};

const elevations = {
  low: "0px 1px 3px rgba(0, 0, 0, 0.1)",
  md: "0px 3px 6px rgba(0, 0, 0, 0.15)",
  high: "0px 6px 12px rgba(0, 0, 0, 0.2)",
};

const transitions = {
  default: "all 0.3s ease-in-out",
  fast: "all 0.2s ease-in-out",
};

const breakpoints = {
  mobile: "480px",      // 📱 Small mobile screens
  tablet: "835px",      // 📲 Tablets and larger phones
  laptop: "1024px",     // 💻 Small laptops & notebooks
  desktop: "1440px",    // 🖥️ Standard desktop screens
  ultraWide: "1920px",  // 🖥️ Ultra-wide screens
};
export type Breakpoint = keyof typeof breakpoints;

const gradients_colors = {
  primary: "linear-gradient(135deg, #B7C92F, #7CA22A)",
  secondary: "linear-gradient(135deg, #F26B5A, #C94739)",
  accent: "linear-gradient(135deg, #F4A259, #D98245)",
  success: "linear-gradient(135deg, #66BB6A, #4CAF50)",
  warning: "linear-gradient(135deg, #FFAB00, #FF8C00)",
  error: "linear-gradient(135deg, #E53935, #D32F2F)",
  info: "linear-gradient(135deg, #29B6F6, #0288D1)",

  /* ✅ Special Variants */
  radialPrimary: "radial-gradient(circle, #B7C92F 0%, #7CA22A 100%)",
  radialSecondary: "radial-gradient(circle, #F26B5A 0%, #C94739 100%)",
  conicAccent: "conic-gradient(from 180deg at 50% 50%, #F4A259, #D98245, #F4A259)",
};


const gradients = (colors: { primary: any; primaryHover?: string; primarySelected: any; secondary: any; secondaryHover?: string; secondarySelected: any; accent: any; accentHover?: string; accentSelected: any; background?: string; backgroundHover?: string; surface?: string; surfaceHover?: string; overlay?: string; textPrimary?: string; textSecondary?: string; textDisabled?: string; border?: string; divider?: string; buttonBackground?: string; buttonText?: string; buttonDisabled?: string; buttonDisabledText?: string; success: any; warning: any; error: any; info: any; } ) => ({
  primary: `linear-gradient(135deg, ${colors.primary}, ${colors.primarySelected})`,
  secondary: `linear-gradient(135deg, ${colors.secondary}, ${colors.secondarySelected})`,
  accent: `linear-gradient(135deg, ${colors.accent}, ${colors.accentSelected})`,
  success: `linear-gradient(135deg, ${colors.success}, ${colors.success})`,
  warning: `linear-gradient(135deg, ${colors.warning}, ${colors.warning})`,
  error: `linear-gradient(135deg, ${colors.error}, ${colors.error})`,
  info: `linear-gradient(135deg, ${colors.info}, ${colors.info})`,

  /* ✅ Special Variants */
  radialPrimary: `radial-gradient(circle, ${colors.primary} 0%, ${colors.primarySelected} 100%)`,
  radialSecondary: `radial-gradient(circle, ${colors.secondary} 0%, ${colors.secondarySelected} 100%)`,
  conicAccent: `conic-gradient(from 180deg at 50% 50%, ${colors.accent}, ${colors.accentSelected}, ${colors.accent})`,
});

const lightColors = {
  primary: "#B7C92F",  // Richer Lime-Green
  primaryHover: "#9EC42E",  // More natural green tone
  primarySelected: "#7CA22A",  // Stronger green for selection states

  secondary: "#F26B5A",  // Coral Orange
  secondaryHover: "#E05545",
  secondarySelected: "#C94739",

  accent: "#F4A259",
  accentHover: "#E6934F",
  accentSelected: "#D98245",

  background: "#F8FAF9",
  backgroundHover: "#FFFFFF",

  surface: "#FEFEFE",
  surfaceHover: "#FFFFFF",
  surfaceDisabled: "rgba(255, 255, 255, 0.5)",
  surfaceEntry: "#FEFEFE",//FEFEFE
  surfaceAlt: "#eef2f9",

  surfaceGlass: "rgba(255, 255, 255, 0.05)",
  borderGlass : "rgba(255, 255, 255, 0.2)",


  overlay: "rgba(0, 0, 0, 0.5)",

  textPrimary: "#1A1A1A",
  textonPrimary: "#F8FAF9",
  textSecondary: "#4E4E4E",
  textonSecondary: "#F8FAF9",
  textDisabled: "#A8A8A8",
  muted: "#A8A8A8",

  border: "#DADADA",
  divider: "#E0E0E0",

  buttonBackground: "#C2D540",
  buttonText: "#FFFFFF",
  buttonDisabled: "#E0E0E0",
  buttonDisabledText: "#A0A0A0",

  pill: "#FFFFFF",

  success: "#66BB6A",
  warning: "#FFAB00",
  error: "#E53935",
  info: "#29B6F6",
};

export const lightTheme = {
  fonts,
  typography,
  zIndices,
  spacing,
  radii,
  elevations,
  transitions,
  breakpoints,
  border,
  colors: lightColors,
  gradients: gradients(lightColors), // ✅ Using dynamic theme colors

  shadow: "rgba(0, 0, 0, 0.1) 0px 0px 0px",
};

const darkColors = {
  ...lightTheme.colors,
  primary: "#C13E80",
  primaryHover: "#A8376E",
  primarySelected: "#8F2D5C",

  secondary: "#C2D540",
  secondaryHover: "#AABF35",
  secondarySelected: "#8CA12B",

  background: "#121212",
  surface: "#1E1E1E",
  overlay: "rgba(255, 255, 255, 0.05)",

  textPrimary: "#F0F0F0",
  textSecondary: "#B0B0B0",
  textDisabled: "#6E6E6E",

  border: "#3A3A3A",
  divider: "#444444",

  buttonBackground: "#C2D540",
  buttonText: "#F0F0F0",
  buttonDisabled: "#3E3E3E",
  buttonDisabledText: "#A0A0A0",

  success: "#66BB6A",
  warning: "#FFAB00",
  error: "#CF6679",
  info: "#80D44A",
};

export const darkTheme = {
  ...lightTheme,
  colors: { ...lightColors, ...darkColors }, // ✅ Override colors for dark mode
  gradients: gradients(darkColors), // ✅ Using dynamic theme colors

};

export type AppTheme = typeof lightTheme;

export const getColorVariant = (
  color: string,
  variant: "Hover" | "Selected",
  colors: typeof lightColors
) => {
  const key = `${color}${variant}` as keyof typeof colors;
  if (!colors[key]) {
    console.warn(`Missing color variant: ${key}`);
  }
  return colors[key]};

  export const themeUtils = {
    getColorVariant,
  };

  export const isValidColorKey = (key: string, themeColors: typeof lightColors): key is keyof typeof lightColors => {
    return key in themeColors;
  };